import type { FocusTrap } from 'focus-trap'
import { createFocusTrap } from 'focus-trap'
import { use, useCallback, useEffect, useRef } from 'react'
import { AriaHiddenContext } from '@/contexts/accessibility/AriaHiddenContext'

interface UseFocusTrapOptions {
  onActivate?: () => void
  onDeactivate?: () => void
  hideBackground?: boolean
}

export function useFocusTrap<T extends HTMLElement = HTMLElement>(options: UseFocusTrapOptions = {}) {
  const {
    onActivate,
    onDeactivate,
    hideBackground = true,
  } = options

  const trapRef = useRef<T | null>(null)
  const focusTrapInstanceRef = useRef<FocusTrap | null>(null)
  const isActivatedRef = useRef(false)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const ariaHiddenContext = use(AriaHiddenContext)

  const activate = useCallback(() => {
    const element = trapRef.current

    if (!element || isActivatedRef.current) {
      return
    }

    try {
      focusTrapInstanceRef.current = createFocusTrap(element, {
        onActivate: () => {
          onActivate?.()
        },
        onDeactivate: () => {
          onDeactivate?.()
        },
        escapeDeactivates: true,
        allowOutsideClick: false,
        clickOutsideDeactivates: false,
        returnFocusOnDeactivate: false,
      })

      focusTrapInstanceRef.current.activate()
      isActivatedRef.current = true

      if (hideBackground) {
        ariaHiddenContext?.hideBackground()
      }
    }
    catch (error) {
      console.warn('Failed to activate focus trap:', error)
    }
  }, [ariaHiddenContext, hideBackground, onActivate, onDeactivate])

  const deactivate = useCallback(() => {
    if (!isActivatedRef.current || !focusTrapInstanceRef.current) {
      return
    }

    try {
      focusTrapInstanceRef.current.deactivate()
      isActivatedRef.current = false

      if (hideBackground) {
        ariaHiddenContext?.showBackground()
      }

      const elementToFocus = returnFocusRef.current
      if (elementToFocus && document.contains(elementToFocus)) {
        elementToFocus.focus()
      }
    }
    catch (error) {
      console.warn('Failed to deactivate focus trap:', error)
    }
  }, [ariaHiddenContext, hideBackground])

  const setReturnFocus = useCallback((element: HTMLElement | null) => {
    returnFocusRef.current = element
  }, [])

  useEffect(() => {
    return () => {
      if (isActivatedRef.current) {
        deactivate()
      }
    }
  }, [deactivate])

  return {
    trapRef,
    activate,
    deactivate,
    setReturnFocus,
    isActive: isActivatedRef.current,
  }
}
